import React, { useMemo } from 'react'
import { $TOWNS } from '@components/$TOWNS/$TOWNS'
import { AvatarStack } from '@components/AvatarStack'
import { useLivePeekData } from '@components/Live/LiveIndicatorPill'
import { useUSDTotalSpaceTips } from '@components/MessageLayout/tips/useUSDTotalSpaceTips'
import { TownsReputationBadge, useReputationData } from '@components/ReputationBadges'
import { ReviewStars } from '@components/ReviewStars/ReviewStars'
import { TownsT } from '@components/TownsT/TownsT'
import { Box, Dot, Icon, Stack, Text } from '@ui'
import { useDevice } from 'hooks/useDevice'
import { useMobile } from 'hooks/useMobile'
import { useReviews } from 'hooks/useReviews'
import { shimmerClass } from 'ui/styles/globals/shimmer.css'
import { addressFromSpaceId, formatOrdinals } from 'ui/utils/utils'
import { env } from 'utils'

import { useUnauthenticatedMembers } from '../hooks/useUnauthenticatedMembers'

interface Props {
    spaceId: string
    networkId: string
    onShowRequirementsModal?: () => void
}

/** Format count to compact display (e.g., 999, 1.2k, 10k, 22.7k) */
function formatCount(count: number): string {
    if (count < 1000) {
        return String(count)
    }
    if (count < 10000) {
        return `${(count / 1000).toFixed(1)}k`
    }
    if (count < 1000000) {
        return `${Math.floor(count / 1000)}k`
    }
    return `${(count / 1000000).toFixed(1)}m`
}

const DISPLAY_LIVE_COUNT = false

export const TownInfo = ({ spaceId, networkId }: Props) => {
    const { averageRating, totalReviews, isLoading: isReviewsLoading } = useReviews(spaceId)
    const { memberIds, isLoading: isMembersLoading } = useUnauthenticatedMembers(networkId)
    const { usdTotal: totalTipsInUsd, isLoading: isTipsLoading } = useUSDTotalSpaceTips({
        spaceId,
        noDecimals: true,
    })
    const isMobile = useMobile()
    const { data: reputationData, hasData: hasReputationData } = useReputationData({
        spaceAddress: addressFromSpaceId(spaceId),
        enabled: true,
    })
    const {
        data: liveData,
        hasActivity: hasLiveActivity,
        enabled: liveEnabled,
    } = useLivePeekData(spaceId)

    const hasReviews = totalReviews > 0
    const isReviewsEnabled = env.VITE_REVIEWS_ENABLED

    const maxMembers = 6
    const displayMembers = useMemo(() => memberIds.slice(0, maxMembers), [memberIds, maxMembers])

    const hasStakingData = hasReputationData

    return (
        <Stack gap="sm">
            {/* Main Info Blocks - Reviews, Entry, Tips */}
            <Stack
                horizontal
                rounded
                paddingX="none"
                paddingY="xs"
                alignItems="center"
                width="100%"
                border="transparent"
            >
                {/* Members Section */}
                <InfoSectionItem
                    title="MEMBERS"
                    centerContent={
                        isMembersLoading || memberIds.length === 0 ? (
                            <Stack horizontal alignItems="center" gap="xs">
                                {Array.from({ length: maxMembers })
                                    .map((_, index) => `k-${index}`)
                                    .map((key) => (
                                        <Box
                                            key={key}
                                            width="x2"
                                            height="x2"
                                            className={shimmerClass}
                                            insetRight="xs"
                                            rounded="full"
                                        />
                                    ))}
                            </Stack>
                        ) : (
                            <Stack horizontal alignItems="center" gap="xs">
                                <AvatarStack userIds={displayMembers} size="avatar_xs" />
                            </Stack>
                        )
                    }
                    bottomContent={
                        isMembersLoading ? (
                            <Box width="x8" height="x2" className={shimmerClass} rounded="xs" />
                        ) : (
                            <Box horizontal centerContent gap="x0.75">
                                <Text size="sm" color="gray2">
                                    {formatCount(memberIds.length)}
                                </Text>
                                {liveEnabled && liveData && liveData.subscriberCount > 0 ? (
                                    <>
                                        <Dot
                                            alignSelf="center"
                                            background="positive"
                                            width="x0.5"
                                            height="x0.5"
                                        />

                                        <Text as="span" display="inline" size="sm" color="positive">
                                            {' '}
                                            {formatCount(liveData.subscriberCount)} online
                                        </Text>
                                    </>
                                ) : (
                                    <Text as="span" display="inline" size="sm" color="gray2">
                                        {`member${memberIds.length !== 1 ? 's' : ''}`}
                                    </Text>
                                )}
                            </Box>
                        )
                    }
                />

                {/* Membership Section */}
                <Delimiter />

                {/* Reviews Section */}
                {isReviewsEnabled && (
                    <InfoSectionItem
                        title={`${totalReviews !== 0 ? `${totalReviews} ` : 'No '}Review${totalReviews !== 1 ? 's' : ''}`}
                        centerContent={
                            isReviewsLoading ? (
                                <Box width="x6" height="x3" className={shimmerClass} rounded="xs" />
                            ) : (
                                <Text size="lg" fontWeight="strong" textAlign="center">
                                    {averageRating.toFixed(1)}
                                </Text>
                            )
                        }
                        bottomContent={
                            isReviewsLoading ? (
                                <Box
                                    width="x12"
                                    height="x3"
                                    className={shimmerClass}
                                    rounded="xs"
                                />
                            ) : (
                                <ReviewStars rating={hasReviews ? averageRating : 0} size={16} />
                            )
                        }
                    />
                )}

                <Delimiter />

                {/* Tips Section */}
                <InfoSectionItem
                    title="Tips"
                    centerContent={
                        isTipsLoading ? (
                            <Stack horizontal alignItems="center" gap="xs">
                                <Box
                                    width="x3"
                                    height="x3"
                                    className={shimmerClass}
                                    rounded="full"
                                />
                                <Box width="x8" height="x3" className={shimmerClass} rounded="xs" />
                            </Stack>
                        ) : (
                            <Stack horizontal alignItems="center" gap="xs">
                                <Icon type="dollarFilled" color="cta1" size="square_sm" />
                                <Text size="lg" fontWeight="strong" textAlign="center">
                                    {totalTipsInUsd || '$0.00'}
                                </Text>
                            </Stack>
                        )
                    }
                    bottomContent={
                        isTipsLoading ? (
                            <Box width="x8" height="x2" className={shimmerClass} rounded="xs" />
                        ) : (
                            <Text size="sm" color="gray2">
                                Sent
                            </Text>
                        )
                    }
                />

                {/* Live Section - only show if live is enabled and there's activity */}
                {DISPLAY_LIVE_COUNT && liveEnabled && hasLiveActivity && liveData && (
                    <>
                        <Delimiter />
                        <InfoSectionItem
                            title="Live Now"
                            centerContent={
                                <Stack horizontal alignItems="center" gap="xs" color="positive">
                                    <Icon type="microphone" size="square_sm" />
                                    <Text size="lg" fontWeight="strong" textAlign="center">
                                        {liveData.stageCount + liveData.listenerCount}
                                    </Text>
                                </Stack>
                            }
                        />
                    </>
                )}

                {!isMobile && hasStakingData && <Delimiter />}
                {!isMobile && hasStakingData && (
                    <StakingInfoSection reputationData={reputationData} />
                )}
            </Stack>

            {/* Staking Section - Separate on mobile */}
            {isMobile && hasStakingData && (
                <Stack
                    horizontal
                    rounded="md"
                    paddingX="none"
                    paddingY="xs"
                    alignItems="center"
                    width="100%"
                    border="transparent"
                    maxWidth="100%"
                    maxHeight="100%"
                >
                    <StakingInfoSection reputationData={reputationData} />
                </Stack>
            )}
        </Stack>
    )
}

const Delimiter = () => {
    return (
        <Box paddingY="sm" width="1" height="100">
            <Box grow borderRight="transparent" width="1" />
        </Box>
    )
}

interface InfoSectionItemProps {
    title: string
    centerContent: React.ReactNode
    bottomContent?: React.ReactNode
    onClick?: () => void
    hoverable?: boolean
    style?: React.CSSProperties
}

export const InfoSectionItem = ({
    title,
    centerContent,
    bottomContent,
    onClick,
    hoverable = false,
    style,
}: InfoSectionItemProps) => {
    const { isTouch } = useDevice()

    return (
        <Stack
            grow
            centerContent
            gap
            height="100"
            position="relative"
            style={{ flex: 1, ...style }}
            cursor={hoverable ? 'pointer' : 'default'}
            hoverable={hoverable}
            {...(onClick ? { [isTouch ? 'onMouseDown' : 'onClick']: onClick } : {})}
        >
            <Box justifyContent="end" height="x1">
                <Text
                    size="sm"
                    color="gray2"
                    textTransform="uppercase"
                    fontWeight="strong"
                    style={{
                        letterSpacing: -0.8,
                    }}
                >
                    {title}
                </Text>
            </Box>

            <Box height="x2" width="100%" justifyContent="center" alignItems="center">
                {centerContent}
            </Box>

            <Box centerContent height="x1" justifyContent="start">
                {bottomContent}
            </Box>
        </Stack>
    )
}

type ReputationData = ReturnType<typeof useReputationData>['data']

const StakingInfoSection = (props: { reputationData: ReputationData }) => {
    const { reputationData } = props

    const isMobile = useMobile()

    return isMobile ? (
        <Stack horizontal centerContent grow gap="sm" paddingX="md" paddingY="xs">
            <Stack horizontal centerContent gap="xxs" color="default">
                <Box height="paragraph">
                    <TownsT />
                </Box>
                <Text size="sm" fontWeight="strong">
                    <$TOWNS noDecimals value={reputationData?.totalStaked ?? BigInt(0)} /> STAKED
                </Text>
            </Stack>
            <Text size="sm" color="gray2">
                {formatOrdinals(reputationData?.rank ?? 0)} Overall
            </Text>
        </Stack>
    ) : (
        <InfoSectionItem
            title="Staked"
            centerContent={
                <Stack gap alignItems="center">
                    <TownsReputationBadge
                        big
                        bold
                        gap="xs"
                        totalStaked={reputationData?.totalStaked ?? BigInt(0)}
                        color="default"
                    />
                </Stack>
            }
            bottomContent={
                reputationData?.rank && (
                    <Text size="sm" color="gray2">
                        {formatOrdinals(reputationData.rank)} Overall
                    </Text>
                )
            }
        />
    )
}
